import React, {useState, useEffect, useRef} from "react";
import { View } from "react-native";
import { Button, Glyphicon, Tooltip, OverlayTrigger } from "react-bootstrap";
import { Player, BigPlayButton, ControlBar, VolumeMenuButton } from "video-react";
import { getEvent, getCapture, getEventVideo, getEventImage, deleteEventVideo } from "../utils/bedrockAPI";
import { getUser, FOOTAGE_BUFFER_MINUTES, isEventTooNewForFootage, inferEventCameraOffsetMinutes } from "../utils/Common";
import "video-react/dist/video-react.css"
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import "./MotionEvent.css"

export default function MotionEvent({eventId,reloadEvents,playbackSpeed,setShowFootage,setFootageStartDate,setFootageMonitor,setFootageCameraOffset,capArchitecture,props}) {
    const [event, setEvent] = useState(null);
    const [videoUrl, setVideoUrl] = useState(null);
    // const [capture, setCapture] = useState(null);
    const [badEvent, setBadEvent] = useState(false);
    const [buttonSize, setButtonSize] = useState("large");
    const [showCapture, setShowCapture] = useState(false);
    const [eventImage, setEventImage] = useState(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const playerRef = useRef(null);
    const imageStageRef = useRef(null);

    useEffect(() => {
        if(window.innerWidth<767) {
            setButtonSize("small");
        }
        function handleFullscreenChange() {
            setIsFullscreen(Boolean(document.fullscreenElement || document.webkitFullscreenElement));
        }
        document.addEventListener("fullscreenchange", handleFullscreenChange);
        document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
        return () => {
            document.removeEventListener("fullscreenchange", handleFullscreenChange);
            document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        };
    },[])

    function toggleImageFullscreen() {
        const el = imageStageRef.current;
        if(!el) return;
        if(!document.fullscreenElement && !document.webkitFullscreenElement) {
            if(el.requestFullscreen) {
                el.requestFullscreen();
            } else if(el.webkitRequestFullscreen) {
                el.webkitRequestFullscreen();
            }
        } else {
            if(document.exitFullscreen) {
                document.exitFullscreen();
            } else if(document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        }
    }

    useEffect(() => {
        let isMounted = true;
        async function loadEvent() {
            try {
                const tempEvent = await getEvent(eventId);
                if(isMounted) {
                    setEvent(tempEvent);
                }
            } catch(e) {
                if(isMounted) setBadEvent(true);
            }
        }
        if(eventId) {
            loadEvent();
        }
        return () => { isMounted = false; };
    },[eventId]);

    useEffect(() => {
        let isMounted = true;
        // async function loadCapture() {
        //     try {
        //         const tempCapture = await getCapture(event.captureId);
        //         if(isMounted) setCapture(tempCapture);
        //     } catch(e) {
        //         // pass
        //     }
        // }
        async function loadVideoUrl() {
            try {
                const tempVideoUrl = await getEventVideo(event.monitorId,event.eventId);
                if(isMounted) setVideoUrl(tempVideoUrl);
            } catch(e) {
                if(isMounted) setBadEvent(true);
            }
        }
        async function loadEventImage() {
            try {
                const tempEventImage = await getEventImage(event.eventId);
                if(isMounted) setEventImage(tempEventImage);
            } catch(e) {
                // pass - Event may not have capture
            }
        }
        if(event) {
            loadVideoUrl();
            // console.log(inferEventCameraOffsetMinutes(event));
        }
        if(event && event.captureId) {
            // loadCapture();
            loadEventImage();
        }
        return () => { isMounted = false; };
    },[event]);

    useEffect(() => {
        if(playbackSpeed) {
            if(playerRef.current) {
                playerRef.current.playbackRate = playbackSpeed;
            }
        }
    },[playbackSpeed]);

    const snapshotTooltip = (
        <Tooltip id="snapshot" style={{fontSize:"16px"}} disabled={buttonSize==="small"}>
            {showCapture ? "Switch to Motion Video" : "Switch to High Resolution Snapshot"}
        </Tooltip>
    );

    const downloadTooltip = (
        <Tooltip id="download" style={{fontSize:"16px"}} disabled={buttonSize==="small"}>
            Right Click → Save-As to Download the File
        </Tooltip>
    );

    const deleteTooltip = (
        <Tooltip id="delete" style={{fontSize:"16px"}} disabled={buttonSize==="small"}>
            Delete the Motion Event and Snapshot
        </Tooltip>
    );

    const eventTooNewForFootage = event ? isEventTooNewForFootage(event) : true;

    const moreFootageTooltip = (
        <Tooltip id="moreFootage" style={{fontSize:"16px"}} disabled={buttonSize==="small"}>
            {eventTooNewForFootage
                ? `Footage is not yet available. End time must be at least ${FOOTAGE_BUFFER_MINUTES} minutes in the past.`
                : "Request footage around this time"}
        </Tooltip>
    );

    const copyEventIdTooltip = (
        <Tooltip id="copyEventId" style={{fontSize:"16px"}} disabled={buttonSize==="small"}>
            Copy event ID
        </Tooltip>
    );

    const fullscreenTooltip = (
        <Tooltip id="fullscreen" style={{fontSize:"16px"}} disabled={buttonSize==="small"}>
            View the image in full screen
        </Tooltip>
    );

    async function removeEvent() {
        if(window.confirm(`Are you sure you wish to delete this event?`)) {
            await deleteEventVideo(event.eventId);
            reloadEvents();
        }    
    }

    return (
        <div className="motion-event">
            {event && videoUrl && !badEvent ? (
                <View style={{flex:1,flexDirection:"column",justifyContent:"center"}}>
                    <div className="motion-event-controls">
                        { getUser().role==='ADMIN' && event && event.eventId && (
                            <OverlayTrigger placement="top" overlay={copyEventIdTooltip}>
                                <Button
                                    className="motion-event-btn"
                                    onClick={() => navigator.clipboard.writeText(event.eventId)}
                                >
                                    <Glyphicon glyph="copy" />
                                </Button>
                            </OverlayTrigger>
                        )}
                        <OverlayTrigger placement="top" overlay={snapshotTooltip}>
                            <Button
                                className="motion-event-btn motion-event-btn--toggle"
                                onClick={() => setShowCapture(!showCapture)}
                                disabled={!eventImage || !eventImage.url}
                            >
                                {!showCapture ? "HD Image" : "Motion Video" }
                            </Button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={downloadTooltip}>
                            <Button
                                className="motion-event-btn"
                                href={(showCapture && eventImage && eventImage.url) ? eventImage.url : videoUrl.url}
                            >
                                Download
                            </Button>
                        </OverlayTrigger>
                        { capArchitecture && (
                            <OverlayTrigger placement="top" overlay={moreFootageTooltip}>
                                <Button
                                    className="motion-event-btn"
                                    disabled={eventTooNewForFootage}
                                    onClick={() => {
                                            if (eventTooNewForFootage) return;
                                            setFootageStartDate(event.startDate);
                                            setFootageMonitor(event.monitorId);
                                            if (setFootageCameraOffset) setFootageCameraOffset(inferEventCameraOffsetMinutes(event));
                                            setShowFootage(true);
                                        }}
                                >
                                    See More
                                </Button>
                            </OverlayTrigger>
                        )}
                        { showCapture && eventImage && eventImage.url && (
                            <OverlayTrigger placement="top" overlay={fullscreenTooltip}>
                                <Button
                                    className="motion-event-btn"
                                    aria-label="Full screen"
                                    onClick={toggleImageFullscreen}
                                >
                                    <Glyphicon glyph="fullscreen" />
                                </Button>
                            </OverlayTrigger>
                        )}
                    </div>
                    {showCapture && eventImage && eventImage.url ? (
                        <View style={{flex:1,flexDirection:"row",justifyContent:"center",width:"100%"}}>
                            <TransformWrapper wrapperStyle={{width:"100%"}}>
                                {() => (
                                    <div className="motion-event-image" ref={imageStageRef}>
                                        <TransformComponent
                                            wrapperStyle={{width:"100%", height:"100%"}}
                                            contentStyle={{width:"100%", height:"100%"}}
                                        >
                                            <img src={eventImage.url} alt="" />
                                        </TransformComponent>
                                        {isFullscreen && (
                                            <button
                                                type="button"
                                                className="motion-event-fullscreen-exit"
                                                onClick={toggleImageFullscreen}
                                            >
                                                <Glyphicon glyph="resize-small" /> Exit full screen
                                            </button>
                                        )}
                                    </div>
                                )}
                            </TransformWrapper>
                        </View>
                    ) : (
                        <View style={{flex:1,flexDirection:"row",justifyContent:"center"}}>
                            <div className="motion-event-video">
                                <Player ref={playerRef}>
                                    <source src={videoUrl.url} />
                                    <BigPlayButton position="center" />
                                    <ControlBar>
                                        <VolumeMenuButton disabled />
                                    </ControlBar>
                                </Player>
                            </div>
                        </View>
                    )}
                </View>
            ) : (
                <div className="motion-event-error">
                    Unable to get motion event video, try again in a little bit
                </div>
            )}
        </div>
    )
}